import React, { useEffect, useState } from 'react'
import { authService } from '../../services/auth'
import { setApiAuthToken, setApiTenantContext } from '../../services/api'
import { useI18n } from '../../i18n/I18nContext'

export const PasswordRecoveryForm: React.FC<{ complete: boolean }> = ({ complete }) => {
  const { t } = useI18n()
  const [token, setToken] = useState(() => complete ? new URLSearchParams(window.location.hash.slice(1)).get('token') || '' : '')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (!complete) return
    const receiveLink = () => {
      const incoming = new URLSearchParams(window.location.hash.slice(1)).get('token')
      if (incoming) { setToken(incoming); setDone(false); setError(''); setPassword(''); setConfirm('') }
      // The secret lives only in this form's memory after navigation. Also
      // accept a newly opened fragment link while this page is already open.
      window.history.replaceState(null, '', window.location.pathname)
    }
    receiveLink()
    window.addEventListener('hashchange', receiveLink)
    return () => window.removeEventListener('hashchange', receiveLink)
  }, [complete])
  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    setError('')
    if (complete && (password !== confirm || password.length < 8 || new TextEncoder().encode(password).length > 72)) {
      setError(t('Use matching passwords with at least 8 characters and at most 72 bytes.'))
      return
    }
    setBusy(true)
    try {
      if (complete) {
        await authService.completePasswordRecovery(token, password)
        setApiAuthToken(null)
        setApiTenantContext(null)
        setToken(''); setPassword(''); setConfirm('')
      } else await authService.startPasswordRecovery(email)
      setDone(true)
    } catch (failure: any) {
      setError(complete && failure.response?.status === 400
        ? t('This recovery link is invalid or expired. Request a new link.')
        : failure.response?.status === 429 ? t('Too many recovery attempts. Try again later.') : t('Recovery could not be completed. Try again later.'))
    } finally { setBusy(false) }
  }
  return <main className="auth-layout"><section className="panel stack" style={{ padding: '1.5rem', maxWidth: 480, margin: '2rem auto' }}>
    <h1>{t(complete ? 'Choose a new password' : 'Recover your account')}</h1>
    {done ? <p role="status">{t(complete ? 'Password reset completed. Sign in again.' : 'If recovery is available for this account, you will receive an email with instructions.')}</p>
      : complete && !token ? <p role="alert">{t('This recovery link is invalid or expired. Request a new link.')}</p>
        : <form className="stack" onSubmit={submit}>
          {complete ? <>
            <label htmlFor="recovery-password">{t('New password')}</label>
            <input id="recovery-password" type="password" className="input" autoComplete="new-password" value={password} onChange={event => setPassword(event.target.value)} required minLength={8} />
            <label htmlFor="recovery-confirm">{t('Confirm password')}</label>
            <input id="recovery-confirm" type="password" className="input" autoComplete="new-password" value={confirm} onChange={event => setConfirm(event.target.value)} required minLength={8} />
          </> : <>
            <label htmlFor="recovery-email">{t('Email')}</label>
            <input id="recovery-email" type="email" className="input" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} required />
          </>}
          {error && <p role="alert" className="error-text">{error}</p>}
          <button type="submit" className="btn btn-primary" disabled={busy}>{t(busy ? 'Submitting…' : complete ? 'Reset password' : 'Send recovery instructions')}</button>
        </form>}
    <a href="/login">{t('Return to sign in')}</a>
    {complete && !done && <a href="/forgot-password">{t('Request a new recovery link')}</a>}
  </section></main>
}
